import React, {FC, useEffect, useState} from "react";
import HotelRoom from "../../../hotels/types/HotelRoom";
import useTranslate from "../../../core/languages/hooks/useTranslate";
import useCustomerContext from "../../../customers/hooks/useCustomerContext";
import ModalRoomImagesGallery from "../../../hotelEventRoom/components/ModalRoomImagesGallery";
import moment from "moment";
import {useFormContext} from "react-hook-form";
import ButtonPrimary from "../../../ui/shared/Button/ButtonPrimary";
import Prebooking from "../../../booking/types/Prebooking";
import RoomHotelEventCardImage from "../../../hotelEventRoom/components/RoomHotelEventCardImage";
import HotelWizardRoomFeedbackModal from "../modals/HotelWizardRoomFeedbackModal";
import HotelWizardRoomDateSelectorModal from "../modals/HotelWizardRoomDateSelectorModal";
import {useRoomCardLogic} from "../../hooks/useRoomCardLogic";
import {getRoomImages} from "../../../common/utils/getRoomImages";
import useDeletePrebookingRoom from "../../../booking/hooks/useDeletePrebookingRoom";
import BasketItem from "../../../booking/types/BasketItem";
import {useIsRoomSelected} from "../../hooks/useIsRoomSelected";
import EventCity from "../../../eventCities/types/EventCity";
import {WizardSaveInput} from "../../types/WizardSaveInput";

export interface HotelPackageRoomCardProps {
  preBooking: Prebooking;
  hotelRoom: HotelRoom;
  accommodationDates?: {
    startDate: string,
    endDate: string
  };
  preBookingHotelRooms?: BasketItem[];
  eventCity: EventCity;
}

const HotelPackageRoomCard: FC<HotelPackageRoomCardProps> = ({
                                                               preBooking,
                                                               hotelRoom,
                                                               accommodationDates,
                                                               preBookingHotelRooms,
                                                               eventCity
                                                             }) => {
  const t = useTranslate();
  const {languageCode} = useCustomerContext();
  const {watch, setValue} = useFormContext<WizardSaveInput>();
  const selectedRoomId = watch('selectedRoomId');
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [isDeletingRoom, setIsDeletingRoom] = useState(false);
  const isSelected = useIsRoomSelected(hotelRoom.id, preBookingHotelRooms);
  const {save: deleteItem} = useDeletePrebookingRoom();

  // Sincronizar selectedRoomId cuando isSelected es true
  useEffect(() => {
    if (isSelected) {
      setValue('selectedRoomId', hotelRoom.id.toString());
    }
  }, [isSelected, hotelRoom.id, setValue]);

  // Delete room logic
  const handleDeleteRoom = () => {
    setIsDeletingRoom(true);
    // Find the prebooking room that matches this hotelRoom
    const matchingRoom = preBookingHotelRooms?.find(room =>
      room.roomHotelEvent === `/room_hotel_events/${hotelRoom.id}`
    );
    if (!matchingRoom) {
      setIsDeletingRoom(false);
      return
    }
    deleteItem({
      prebookingInternalId: preBooking.internalId || "",
      itemId: matchingRoom?.id || 0,
    }, {
      onSuccess: () => {
        setValue('selectedRoomId', null);
        setIsDeletingRoom(false);
      },
      onError: () => {
        setIsDeletingRoom(false);
      }
    });
  };

  const {
    isDateSelectorOpen,
    setIsDateSelectorOpen,
    modalIsOpen,
    setModalIsOpen,
    isCheckingAvailability,
    isAddingRoomLoading,
    unavailableDates,
    overbookingDates,
    errorMessage,
    handleDatesConfirmed,
    handleConfirmAddRoom,
    modalKind,
  } = useRoomCardLogic({
    preBookingInternalId: preBooking?.internalId,
    hotelRoom,
    startDate: accommodationDates?.startDate,
    endDate: accommodationDates?.endDate,
  });

  // Map images to use principalImgContentUrl when contentUrl is null
  const roomImages = getRoomImages(hotelRoom);

  return (
    <div className="listingSectionSidebar__wrap shadow-xl mb-5">
      {/* TITLE */}
      <div className="flex justify-between mb-4 items-center">
        <span className="text-1xl">
          <i className="text-2xl las la-bed la-fw" style={{marginRight: "5px"}}></i> {hotelRoom.trType[languageCode]}
        </span>
      </div>
      {/* CONTENT WITH IMAGE - Use flex layout when image exists */}
      <div className="flex gap-3">
        {/* IMAGE */}
        {hotelRoom.principalImgContentUrl && (
          <RoomHotelEventCardImage hotelRoom={hotelRoom} roomImages={roomImages} setIsGalleryOpen={setIsGalleryOpen}/>
        )}
        {/* INFORMATION */}
        <div className="flex flex-col space-y-3 flex-1">
          <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
            <span>{t("hotelDetail.from")}</span>
            <span>{moment(accommodationDates?.startDate).format("DD/MM/YYYY")}</span>
          </div>
          <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
            <span>{t("hotelDetail.to")}</span>
            <span>{moment(accommodationDates?.endDate).format("DD/MM/YYYY")}</span>
          </div>
          <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
            <span>{t("hotelDetail.capacity")}</span>
            <span>
              {[...Array(hotelRoom.adults)].map((index) =>
                <i className="las la-user la-lg" key={index + hotelRoom.id}></i>
              )}
              {[...Array(hotelRoom.children)].map((index) =>
                <i className="text-xl las la-user" key={index + hotelRoom.id}></i>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* BUTTON */}
      <ButtonPrimary
        type="button"
        onClick={(e) => {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          if (isSelected) {
            handleDeleteRoom();
          } else {
            setIsDateSelectorOpen(true);
          }
        }}
        disabled={!isSelected && selectedRoomId !== null && selectedRoomId !== undefined}
        className={`uppercase mt-4 w-full font-medium transition px-6 py-2 rounded-lg ${
          isSelected
            ? 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white'
            : selectedRoomId !== null && selectedRoomId !== undefined
              ? 'bg-neutral-400 dark:bg-neutral-600 hover:bg-neutral-400 dark:hover:bg-neutral-600 !text-neutral-600 dark:!text-neutral-300 cursor-not-allowed opacity-60'
              : ''
        }`}
        loading={isSelected ? isDeletingRoom : (isCheckingAvailability || isAddingRoomLoading)}
      >
        <i className={`text-2xl la-fw ${isSelected ? 'las la-trash-alt' : 'las la-shopping-cart'}`}></i>
        {isSelected ? t("common.actions.unSelect") : t("packages.fields.selectRoom")}
      </ButtonPrimary>

      {modalKind !== "roomAdded" && (
        <HotelWizardRoomFeedbackModal
          kind={modalKind}
          isOpen={modalIsOpen}
          onClose={() => {
            setModalIsOpen(false);
          }}
          onConfirmAddRoom={handleConfirmAddRoom}
          overbookingDates={overbookingDates}
          unavailableDates={unavailableDates}
          errorMessage={errorMessage}
        />
      )}

      {/* Date Selector Modal */}
      <HotelWizardRoomDateSelectorModal
        isOpen={isDateSelectorOpen}
        onClose={() => setIsDateSelectorOpen(false)}
        onConfirm={handleDatesConfirmed}
        eventCity={eventCity}
        minStartDate={accommodationDates?.startDate || ""}
        maxEndDate={accommodationDates?.endDate || ""}
        isPackageDates={true}
      />

      {/* ROOM IMAGES GALLERY MODAL */}
      {roomImages.length > 0 && (
        <ModalRoomImagesGallery
          roomImages={roomImages}
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
          initFocus={0}
        />
      )}
    </div>
  );
};

export default HotelPackageRoomCard;


